import { useSelector } from "react-redux";
import classNames from "classnames";
import useTranslate from "hooks/useTranslate";
import Button from "components/atoms/Button";
import { replaceValuesInText } from "utils/replaceValuesInText";
import { currentCurrency } from "redux/user/selectors";
import TimerIcon from "assets/icons/timer.svg?react";
import { useEffect, useState } from "react";
import {
  LoyaltyRewardMiniCardProps,
  LoyaltyRewardMiniCardVariant,
} from "./types";
import { ContentLoyaltyRewardMiniCard } from "./constants";
import "./styles.sass";

const LoyaltyRewardMiniCard = (props: LoyaltyRewardMiniCardProps) => {
  const {
    value,
    variant,
    primaryTitle = false,
    canGet = false,
    image,
    rankName,
    handleClick,
    className,
    isShow = false,
    isRanksPage = false,
    availableUntil,
    cashbackAmount,
  } = props;
  const t = useTranslate();

  const content = ContentLoyaltyRewardMiniCard(t)[variant] as {
    title: string;
    descriptionActive: string;
    descriptionActiveCashback: string;
    titleActive: string;
  };
  const { title, descriptionActive, descriptionActiveCashback, titleActive } =
    content;

  const currencyCode = useSelector(currentCurrency);

  const classNameRoot = classNames(
    "LoyaltyRewardMiniCard",
    className,
    variant.includes("cashback") && !isRanksPage && "cashBackCard",
  );

  const titleClass = classNames(
    "LoyaltyRewardMiniCard__title",
    primaryTitle && "LoyaltyRewardMiniCard__title_primary",
  );

  const imageClass = classNames(
    "LoyaltyRewardMiniCard__image",
    variant === LoyaltyRewardMiniCardVariant.FREE_SPINS &&
      "LoyaltyRewardMiniCard__image_freeSpins",
  );

  const [timeDisplay, setTimeDisplay] = useState<string>("");

  const day = t("loyalty.redesign.1.cashbacks.day");
  const days = t("loyalty.redesign.1.cashback.days");

  useEffect(() => {
    if (!availableUntil) {
      return;
    }

    const calculateTimeRemaining = () => {
      const now = new Date();
      const expiration = new Date(availableUntil);
      const diffMs = expiration.getTime() - now.getTime();

      if (diffMs <= 0) {
        return;
      }

      const diffHours = diffMs / (1000 * 60 * 60);
      const diffDays = Math.floor(diffHours / 24);

      if (diffHours < 24) {
        const hours = Math.floor(diffHours);
        const minutes = Math.floor((diffHours - hours) * 60);
        setTimeDisplay(
          `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`,
        );
      } else if (diffHours >= 24 && diffHours < 48) {
        setTimeDisplay(`1 ${day}`);
      } else if (diffDays >= 2) {
        setTimeDisplay(`${diffDays} ${days}`);
      }
    };

    calculateTimeRemaining();
    const interval = setInterval(calculateTimeRemaining, 60000);

    return () => clearInterval(interval);
  }, [availableUntil, day, days]);

  const cardTitle = () => {
    if (variant.includes("cashback")) {
      if (canGet && cashbackAmount) {
        return replaceValuesInText(t(titleActive), {
          cashbackAmount,
          amount: cashbackAmount,
          currency: currencyCode,
        });
      }
      return replaceValuesInText(t(title), {
        value,
        countFS: value,
      });
    }
    return replaceValuesInText(t(title), {
      value,
      countFS: value,
      currency: currencyCode,
    });
  };

  const cardDescription = () => {
    if (variant.includes("cashback") && canGet) {
      return t(descriptionActiveCashback);
    }
    return canGet || isShow
      ? t(descriptionActive)
      : t("loyalty.redesign.1.text.placeholder");
  };

  return (
    <div className={classNameRoot}>
      <div className="LoyaltyRewardMiniCard__content">
        <div className="LoyaltyRewardMiniCard__left">
          <div>
            <h3 className={titleClass}>{cardTitle()}</h3>
            <p className="LoyaltyRewardMiniCard__description">
              {cardDescription()}
            </p>
            {!canGet && rankName && (
              <span className="LoyaltyRewardMiniCard__descriptionReward">
                {t("loyalty.redesign.1.mini-card.reward-for")}{" "}
                {rankName && (
                  <span className="LoyaltyRewardMiniCard__rankName">
                    {rankName}
                  </span>
                )}
              </span>
            )}
          </div>
          {canGet && (
            <Button
              className="LoyaltyRewardMiniCard__button"
              variation="primary"
              label={t("loyalty.redesign.1.card.mini.button.claim")}
              onClick={handleClick}
            />
          )}
        </div>
        <div>
          {variant.includes("cashback") && !isRanksPage && canGet && (
            <div className="LoyaltyRewardMiniCard__timer">
              <TimerIcon width={16} />
              <span>{timeDisplay}</span>
            </div>
          )}
          <img src={image} alt="reward" className={imageClass} />
        </div>
      </div>
    </div>
  );
};

export default LoyaltyRewardMiniCard;
